import { CommonModule } from '@angular/common'
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges
} from '@angular/core'
import { FormsModule } from '@angular/forms'

import {
    UiDataTableAccessor,
    UiDataTableColumn,
    UiDataTableFilter,
    UiDataTableFilterOption,
    UiDataTableQuery,
    UiDataTableRowAction,
    UiDataTableRowActionEvent,
    UiDataTableSort,
    UiDataTableVariant
} from './ui-data-table.model'
import { UiTableActionsDirective } from './ui-table-actions.directive'
import { UiTableCellDirective } from './ui-table-cell.directive'
import { UiTableExpandedDirective } from './ui-table-expanded.directive'
import { UiTableRowAttrsDirective } from './ui-table-row-attrs.directive'

@Component({
    selector: 'ui-data-table',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        UiTableRowAttrsDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './ui-data-table.component.html',
    styleUrls: ['./ui-data-table.component.scss']
})
export class UiDataTableComponent<T> implements AfterContentInit, OnChanges, OnInit {
    @Input() tableId = 'data-table'
    @Input() caption?: string
    @Input() columns: UiDataTableColumn<T>[] = []
    @Input() data: T[] = []
    @Input() total?: number
    @Input() loading = false
    @Input() error: string | null = null
    @Input() emptyMessage = 'No rows to display'
    @Input() loadingMessage = 'Loading rows'
    @Input() errorMessage = 'Unable to load rows'
    @Input() pageSize = 10
    @Input() pageSizeOptions: number[] = [10, 25, 50, 100]
    @Input() serverSide = false
    @Input() showToolbar = true
    @Input() pagination = true
    @Input() variant: UiDataTableVariant = 'default'
    @Input() rowActions: UiDataTableRowAction<T>[] = []
    @Input() rowKey: ((row: T) => string) | null = null
    @Input() expandedRowKeys: ReadonlySet<string> | string[] = new Set()
    @Input() canExpandRow: ((row: T) => boolean) | null = null
    @Input() expanderHeaderLabel = 'Expand row'
    @Input() expanderTestId?: string
    @Input() rowClass: ((row: T) => string | string[] | null) | null = null
    @Input() rowAttrs: ((row: T) => Record<string, string | null | undefined>) | null = null

    @Output() queryChange = new EventEmitter<UiDataTableQuery>()
    @Output() rowAction = new EventEmitter<UiDataTableRowActionEvent<T>>()
    @Output() rowToggle = new EventEmitter<T>()

    @ContentChildren(UiTableCellDirective)
    cellSlots!: QueryList<UiTableCellDirective<T>>

    @ContentChild(UiTableActionsDirective)
    actionsSlot?: UiTableActionsDirective<T>

    @ContentChild(UiTableExpandedDirective)
    expandedSlot?: UiTableExpandedDirective<T>

    sortState: UiDataTableSort | null = null
    filters: Record<string, UiDataTableFilter['value']> = {}
    visibleColumnIds = new Set<string>()
    currentPageIndex = 0
    currentPageSize = 10
    columnMenuOpen = false

    private cellSlotByColumn = new Map<string, UiTableCellDirective<T>>()
    private readonly booleanFilterOptions: UiDataTableFilterOption[] = [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ]
    private initialized = false

    ngOnInit(): void {
        this.currentPageSize = this.pageSize
        this.syncVisibleColumns()
        this.initialized = true
        this.emitQueryChange()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columns']) {
            this.syncVisibleColumns()
        }

        if (changes['pageSize'] && !changes['pageSize'].firstChange) {
            this.currentPageSize = this.pageSize
            this.currentPageIndex = 0
            this.emitQueryChange()
        }

        if (changes['data'] && !this.serverSide) {
            this.clampPageIndex()
        }
    }

    ngAfterContentInit(): void {
        this.rebuildCellSlotMap()
        this.cellSlots.changes.subscribe(() => this.rebuildCellSlotMap())
    }

    get visibleColumns(): UiDataTableColumn<T>[] {
        return this.columns.filter(column => this.visibleColumnIds.has(column.id))
    }

    get hideableColumns(): UiDataTableColumn<T>[] {
        return this.columns.filter(column => column.hideable !== false)
    }

    get hasActions(): boolean {
        return !!this.actionsSlot || this.rowActions.length > 0
    }

    get hasExpansion(): boolean {
        return !!this.expandedSlot
    }

    get activeFilters(): UiDataTableFilter[] {
        return Object.entries(this.filters)
            .filter(([, value]) => value != null && String(value).trim() !== '')
            .map(([columnId, value]) => ({ columnId, value }))
    }

    get filteredRows(): T[] {
        if (this.serverSide) return this.data

        const active = this.activeFilters
        if (active.length === 0) return this.data

        return this.data.filter(row => active.every(filter => {
            const column = this.columns.find(c => c.id === filter.columnId)
            if (!column) return true
            const value = this.valueFor(row, column, 'filter')
            return String(value ?? '')
                .toLowerCase()
                .includes(String(filter.value ?? '').toLowerCase())
        }))
    }

    get sortedRows(): T[] {
        if (this.serverSide || !this.sortState) return this.filteredRows

        const column = this.columns.find(c => c.id === this.sortState?.columnId)
        if (!column) return this.filteredRows

        const direction = this.sortState.direction === 'asc' ? 1 : -1
        return [...this.filteredRows].sort((a, b) => this.compareRows(a, b, column) * direction)
    }

    get pagedRows(): T[] {
        if (this.serverSide) return this.data
        if (!this.pagination) return this.sortedRows

        const start = this.currentPageIndex * this.currentPageSize
        return this.sortedRows.slice(start, start + this.currentPageSize)
    }

    get resolvedTotal(): number {
        return this.serverSide ? (this.total ?? this.data.length) : this.filteredRows.length
    }

    get pageCount(): number {
        return Math.max(1, Math.ceil(this.resolvedTotal / this.currentPageSize))
    }

    get pageStart(): number {
        if (!this.pagination && this.resolvedTotal > 0) return 1
        if (this.resolvedTotal === 0) return 0
        return this.currentPageIndex * this.currentPageSize + 1
    }

    get pageEnd(): number {
        if (!this.pagination) return this.resolvedTotal
        return Math.min(this.resolvedTotal, (this.currentPageIndex + 1) * this.currentPageSize)
    }

    get expandedColspan(): number {
        return this.visibleColumns.length + (this.hasExpansion ? 1 : 0) + (this.hasActions ? 1 : 0)
    }

    get showEmptyState(): boolean {
        return !this.loading && !this.error && this.pagedRows.length === 0
    }

    customCellTemplate(columnId: string): UiTableCellDirective<T> | null {
        return this.cellSlotByColumn.get(columnId) ?? null
    }

    valueFor(row: T, column: UiDataTableColumn<T>, mode: 'display' | 'sort' | 'filter' = 'display'): unknown {
        const accessor = mode === 'sort'
            ? column.sortAccessor ?? column.accessor
            : mode === 'filter'
                ? column.filterAccessor ?? column.accessor
                : column.accessor

        if (!accessor) return undefined
        return this.readAccessor(row, accessor)
    }

    formatValue(value: unknown): string {
        if (value == null || value === '') return '-'
        if (value instanceof Date) return value.toLocaleString()
        return String(value)
    }

    filterValue(columnId: string): UiDataTableFilter['value'] {
        return this.filters[columnId] ?? ''
    }

    setFilter(column: UiDataTableColumn<T>, value: UiDataTableFilter['value']): void {
        if (!this.isFilterable(column)) return
        this.filters = {
            ...this.filters,
            [column.id]: value
        }
        this.currentPageIndex = 0
        this.emitQueryChange()
    }

    filterOptionsFor(column: UiDataTableColumn<T>): UiDataTableFilterOption[] {
        if (column.filterOptions?.length) return column.filterOptions
        if (column.filterable === 'boolean') return this.booleanFilterOptions
        return []
    }

    clearFilter(columnId: string): void {
        const value = this.filters[columnId]
        if (value == null || String(value).trim() === '') return
        const next = { ...this.filters }
        delete next[columnId]
        this.filters = next
        this.currentPageIndex = 0
        this.emitQueryChange()
    }

    toggleSort(column: UiDataTableColumn<T>): void {
        if (!column.sortable) return

        if (this.sortState?.columnId !== column.id) {
            this.sortState = { columnId: column.id, direction: 'asc' }
        } else if (this.sortState.direction === 'asc') {
            this.sortState = { columnId: column.id, direction: 'desc' }
        } else {
            this.sortState = null
        }

        this.currentPageIndex = 0
        this.emitQueryChange()
    }

    sortLabel(column: UiDataTableColumn<T>): string {
        if (this.sortState?.columnId !== column.id) return 'Sort'
        return this.sortState.direction === 'asc' ? 'Sorted ascending' : 'Sorted descending'
    }

    sortIcon(column: UiDataTableColumn<T>): string {
        if (this.sortState?.columnId !== column.id) return 'fa-sort'
        return this.sortState.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }

    setColumnVisible(column: UiDataTableColumn<T>, visible: boolean): void {
        if (column.hideable === false) return

        const next = new Set(this.visibleColumnIds)
        if (visible) next.add(column.id)
        else next.delete(column.id)

        if (next.size === 0) return

        this.visibleColumnIds = next
        this.emitQueryChange()
    }

    isColumnVisible(column: UiDataTableColumn<T>): boolean {
        return this.visibleColumnIds.has(column.id)
    }

    setPageSize(size: number | string): void {
        const next = Number(size)
        if (!Number.isFinite(next) || next <= 0) return
        this.currentPageSize = next
        this.currentPageIndex = 0
        this.emitQueryChange()
    }

    nextPage(): void {
        if (this.currentPageIndex >= this.pageCount - 1) return
        this.currentPageIndex += 1
        this.emitQueryChange()
    }

    previousPage(): void {
        if (this.currentPageIndex <= 0) return
        this.currentPageIndex -= 1
        this.emitQueryChange()
    }

    actionHidden(action: UiDataTableRowAction<T>, row: T): boolean {
        return this.resolveActionFlag(action.hidden, row)
    }

    actionDisabled(action: UiDataTableRowAction<T>, row: T): boolean {
        return this.resolveActionFlag(action.disabled, row)
    }

    actionClass(action: UiDataTableRowAction<T>): string {
        switch (action.variant) {
            case 'primary':
                return 'btn btn-sm btn-primary'
            case 'danger':
                return 'btn btn-sm btn-outline-danger'
            default:
                return 'btn btn-sm btn-outline-secondary'
        }
    }

    emitRowAction(action: UiDataTableRowAction<T>, row: T, event: Event): void {
        event.stopPropagation()
        if (this.actionDisabled(action, row)) return
        this.rowAction.emit({ action, row })
    }

    cellClass(column: UiDataTableColumn<T>, row: T): string | string[] | null {
        if (!column.cellClass) return null
        return typeof column.cellClass === 'function'
            ? column.cellClass(row)
            : column.cellClass
    }

    headerClass(column: UiDataTableColumn<T>): string | string[] | null {
        return column.headerClass ?? null
    }

    rowClassFor(row: T): string | string[] | null {
        return this.rowClass ? this.rowClass(row) : null
    }

    rowAttrsFor(row: T): Record<string, string | null | undefined> {
        return this.rowAttrs ? this.rowAttrs(row) : {}
    }

    rowKeyFor(row: T, index = 0): string {
        if (this.rowKey) return this.rowKey(row)
        const value = (row as Record<string, unknown>)?.['id']
        return value == null ? String(index) : String(value)
    }

    canExpand(row: T): boolean {
        return this.hasExpansion && (this.canExpandRow ? this.canExpandRow(row) : true)
    }

    isExpanded(row: T, index = 0): boolean {
        if (!this.canExpand(row)) return false
        const key = this.rowKeyFor(row, index)
        return Array.isArray(this.expandedRowKeys)
            ? this.expandedRowKeys.includes(key)
            : this.expandedRowKeys.has(key)
    }

    expandedRowId(row: T, index = 0): string {
        return `${this.tableId}-expanded-${this.rowKeyFor(row, index)}`
    }

    toggleExpanded(row: T, event: Event): void {
        event.stopPropagation()
        if (!this.canExpand(row)) return
        this.rowToggle.emit(row)
    }

    isFilterable(column: UiDataTableColumn<T>): boolean {
        return !!column.filterable
    }

    trackByRow = (index: number, row: T): unknown => row ?? index
    trackByColumn = (_index: number, column: UiDataTableColumn<T>): string => column.id

    private syncVisibleColumns(): void {
        if (!this.columns.length) {
            this.visibleColumnIds = new Set()
            return
        }

        const current = this.visibleColumnIds
        const next = new Set<string>()
        for (const column of this.columns) {
            const defaultVisible = column.visibleByDefault !== false
            if (column.hideable === false || current.has(column.id) || (current.size === 0 && defaultVisible)) {
                next.add(column.id)
            }
        }

        if (next.size === 0) {
            const first = this.columns[0]
            if (first) next.add(first.id)
        }

        this.visibleColumnIds = next

        if (this.initialized) {
            this.emitQueryChange()
        }
    }

    private rebuildCellSlotMap(): void {
        this.cellSlotByColumn = new Map(this.cellSlots.map(slot => [slot.uiTableCell, slot]))
    }

    private readAccessor(row: T, accessor: UiDataTableAccessor<T>): unknown {
        return typeof accessor === 'function'
            ? accessor(row)
            : (row as Record<string, unknown>)[String(accessor)]
    }

    private compareRows(a: T, b: T, column: UiDataTableColumn<T>): number {
        const left = this.valueFor(a, column, 'sort')
        const right = this.valueFor(b, column, 'sort')

        if (left == null && right == null) return 0
        if (left == null) return -1
        if (right == null) return 1

        if (typeof left === 'number' && typeof right === 'number') {
            return left - right
        }

        return String(left).localeCompare(String(right), undefined, {
            numeric: true,
            sensitivity: 'base'
        })
    }

    private clampPageIndex(): void {
        if (this.currentPageIndex <= this.pageCount - 1) return
        this.currentPageIndex = this.pageCount - 1
    }

    private emitQueryChange(): void {
        this.queryChange.emit({
            tableId: this.tableId,
            sort: this.sortState,
            filters: this.activeFilters,
            pagination: {
                pageIndex: this.currentPageIndex,
                pageSize: this.currentPageSize
            },
            visibleColumnIds: Array.from(this.visibleColumnIds)
        })
    }

    private resolveActionFlag(flag: boolean | ((row: T) => boolean) | undefined, row: T): boolean {
        return typeof flag === 'function' ? flag(row) : !!flag
    }
}
